import { CloseOutlined, PlusOutlined, UndoOutlined } from "@ant-design/icons";
import { Button, Card, ConfigProvider, Flex, Table, TableProps, Tooltip } from "antd";
import CardSectionTitle from "components/CardSectionTitle.component";
import { ReadOnlyWhenRestricted } from "components/capability";
import { AdminCapability } from "shared/utils/capabilities";
import { showConfirmModal } from "components/modals/confirmModal";
import { useLeavePreview } from "components/modals/LeaveConflictWarningModal";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import NewLeaveModal from "components/modals/NewLeaveModal.component";
import { useCancelLeave, useRestoreLeave } from "hooks/mutations/staff";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { useState } from "react";
import { th, tnum } from "shared/components/TableText";
import { translate, translateWithVars } from "shared/languages";
import { Leave } from "shared/types";
import { LeaveStatus } from "shared/types/enum";
import {
  getLeaveStatusLabel,
  LEAVE_STATUS_TONE,
  LeaveDisplayStatus,
} from "shared/utils";
import { DEFAULT_DATE_FORMAT, formatDate, now, tzDate } from "shared/utils/dayjs";

// "Last changed by" note for the status badge tooltip — leave has no
// financial stake (unlike booking_v2's full activity log), so a last-actor
// stamp covers the transparency need without a per-transition log table.
// Prefers updated_by (the more recent action); falls back to created_by for
// a leave that's never been touched since; null for legacy rows with neither.
const lastActorNote = (leave: Leave): string | null => {
  const dateFormat = `${DEFAULT_DATE_FORMAT}, HH:mm`;
  if (leave.updated_by_name) {
    return translateWithVars("leave.changed_by", {
      name: leave.updated_by_name,
      date: formatDate(leave.updated_at, dateFormat),
    });
  }
  if (leave.created_by_name) {
    return translateWithVars("leave.applied_by", {
      name: leave.created_by_name,
      date: formatDate(leave.created_at, dateFormat),
    });
  }
  return null;
};

// Only "Cancelled" is a real stored state (see LeaveStatus). "Upcoming" /
// "Ongoing" / "Completed" are derived from start_date/end_date vs now,
// mirroring how ScheduleStatusBadge derives a schedule's own Upcoming/
// Ongoing/Finished from its start/end times rather than storing them — a
// leave spans a date range the same way. Labels are English-only/code-driven
// (getLeaveStatusLabel in shared/utils) to stay consistent with booking/
// schedule status tags elsewhere in the admin, which follow the same rule.
const displayStatus = (leave: Leave): LeaveDisplayStatus => {
  if (leave.status === LeaveStatus.Cancelled) return "cancelled";
  const current = now();
  // Whole-day comparisons (endOf/startOf "day"), not a raw instant compare
  // against the stored midnight timestamp — otherwise a leave flips to
  // "Completed" the moment midnight passes on its last day instead of
  // staying "Ongoing" through the entire day (and "Upcoming" would end a
  // full day early the same way). Matches this file's own tzDate() usage
  // elsewhere (the date-format and num-of-days columns).
  if (current.isAfter(tzDate(leave.end_date).endOf("day"))) return "completed";
  if (current.isBefore(tzDate(leave.start_date).startOf("day"))) return "upcoming";
  return "ongoing";
};

interface TimeOffPageProps {
  staffId: string;
  leaves: Leave[];
}

const DISPLAY_STATUSES: LeaveDisplayStatus[] = ["upcoming", "ongoing", "completed", "cancelled"];

// Cancel is offered for "Completed" too, not just "Upcoming"/"Ongoing" — it
// has zero effect on scheduling once the date's passed (assertInstructorAvailable's
// leave check can never apply to a past occurrence either way), but it's the
// only way to correct a mistakenly-logged historical record now that doing so
// is fully attributable (created_by/updated_by) rather than a silent edit.
// See the different confirm copy below for the completed case.
const isCancellable = (status: LeaveDisplayStatus) =>
  status === "upcoming" || status === "ongoing" || status === "completed";

const TimeOffPage = ({ staffId, leaves = [] }: TimeOffPageProps) => {
  const PAGE_SIZE = 10;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate: cancelLeave, isPending: isCancelling } = useCancelLeave();
  const { mutate: restoreLeave, mutateAsync: restoreLeaveAsync, isPending: isRestoring } = useRestoreLeave();
  // Own useLeavePreview instance (separate from NewLeaveModal's) so restore
  // gets the same conflict check applying a new leave already has — a
  // restored leave can silently conflict with schedules created while it was
  // cancelled, which used to go entirely unwarned.
  const { checkAndConfirm: checkRestoreConflicts, leavePreviewWarning } = useLeavePreview(
    translate("leave.conflict.confirmRestore"),
    translate("leave.conflict.removeAndRestore"),
  );

  const columns: TableProps<Leave>["columns"] = [
    {
      title: th(translate("leave.start_date")),
      dataIndex: "start_date",
      key: "start_date",
      render: (_, { start_date }) => (
        <span style={tnum}>{formatDate(start_date)}</span>
      ),
    },
    {
      title: th(translate("leave.end_date")),
      dataIndex: "end_date",
      key: "end_date",
      render: (_, { end_date }) => (
        <span style={tnum}>{formatDate(end_date)}</span>
      ),
      sorter: (a, b) => tzDate(a.end_date).diff(b.end_date),
      sortOrder: "descend",
    },
    {
      title: th(translate("leave.column_number_of_days")),
      dataIndex: "num_of_days",
      render: (_, { start_date, end_date }) => (
        <span style={tnum}>
          {tzDate(end_date).diff(tzDate(start_date), "day") + 1}
        </span>
      ),
    },
    {
      title: th(translate("leave.column_status")),
      dataIndex: "status",
      key: "status",
      render: (_, leave) => {
        const status = displayStatus(leave);
        const badge = <StatusBadge tone={LEAVE_STATUS_TONE[status]} label={getLeaveStatusLabel(status)} />;
        const note = lastActorNote(leave);
        return note ? <Tooltip title={note}>{badge}</Tooltip> : badge;
      },
      filters: DISPLAY_STATUSES.map((status) => ({
        text: getLeaveStatusLabel(status),
        value: status,
      })),
      onFilter: (value, record) => displayStatus(record) === value,
    },
    {
      title: th(translate("leave.column_action")),
      key: "action",
      align: "left",
      render: (_, leave) => {
        const status = displayStatus(leave);
        if (isCancellable(status)) {
          return (
            <Tooltip title={translate("leave.cancel_button")}>
              <Button
                color="danger"
                variant="link"
                icon={<CloseOutlined />}
                loading={isCancelling}
                onClick={() =>
                  showConfirmModal({
                    tone: "amber",
                    title: translate("leave.cancel_confirm_title"),
                    // A completed leave's dates are already in the past, so
                    // cancelling it can't free up any scheduling — only the
                    // record itself changes.
                    content: translate(
                      status === "completed"
                        ? "leave.cancel_confirm_content_completed"
                        : "leave.cancel_confirm_content",
                    ),
                    confirmLabel: translate("leave.cancel_button"),
                    destructive: true,
                    onConfirm: () => cancelLeave({ leave_id: leave.id, staff_id: staffId }),
                  })
                }
              />
            </Tooltip>
          );
        }
        // Undo for an accidental cancel — restores back to Approved, then the
        // same date-based upcoming/ongoing/completed classification applies
        // automatically, same as if it had never been cancelled. Gated by
        // the same conflict preview "Ajukan Cuti" uses: schedules may have
        // been created for this instructor while the leave sat cancelled,
        // and restoring would otherwise silently resurrect a conflict with
        // them. No conflicts found -> the plain "Undo this leave?" confirm
        // below fires immediately, unchanged from before.
        if (status === "cancelled") {
          const confirmRestore = () =>
            showConfirmModal({
              title: translate("leave.restore_confirm_title"),
              content: translate("leave.restore_confirm_content"),
              confirmLabel: translate("button.undo_void"),
              onConfirm: () => restoreLeave({ leave_id: leave.id, staff_id: staffId }),
            });
          return (
            // "Undo" is the admin's word in both languages (button.undo_void,
            // "Undo cuti ini?"), read through i18n like every other tooltip.
            <Tooltip title={translate("button.undo_void")}>
              <Button
                variant="link"
                icon={<UndoOutlined />}
                loading={isRestoring}
                onClick={() =>
                  checkRestoreConflicts(
                    {
                      staff_id: staffId,
                      start_date: tzDate(leave.start_date).toISOString(),
                      end_date: tzDate(leave.end_date).toISOString(),
                    },
                    confirmRestore,
                    // "Hapus Jadwal & Pulihkan Cuti": one server call deletes
                    // the classes and restores the leave; no second confirm.
                    () =>
                      restoreLeaveAsync({
                        leave_id: leave.id,
                        staff_id: staffId,
                        remove_affected_schedules: true,
                      }).then(() => undefined),
                  )
                }
              />
            </Tooltip>
          );
        }
        // A "Completed" row has no action button, but an invisible spacer
        // the same size as the circular icon buttons above keeps every row
        // the same height (an empty cell otherwise collapses to just the
        // text row's height, making Completed rows visibly shorter).
        return <div style={{ width: 40, height: 40 }} />;
      },
    },
  ];

  return (
    <>
      {/* Modal-buttons theme: navy confirm + ink Batal inside the modal. */}
      <ConfigProvider theme={modalButtonsTheme}>
        <NewLeaveModal
          staffId={staffId}
          open={isModalOpen}
          onCancel={() => setIsModalOpen(!isModalOpen)}
        />
      </ConfigProvider>
      {leavePreviewWarning}
      {/* Pembatasan Admin: the operational-settings toggle names instructor
          availability; the disabled context reaches the add button and the
          row buttons alike. */}
      <ReadOnlyWhenRestricted capability={AdminCapability.OperationalSettings}>
      <Card>
        <Flex vertical gap={18}>
          <Flex justify="space-between" align="flex-start" gap={16}>
            <CardSectionTitle
              title={translate("tabs.time_off")}
              subtitle={translate("leave.subtitle")}
            />
              <Button
                onClick={() => setIsModalOpen(!isModalOpen)}
                type="primary"
                icon={<PlusOutlined />}
              >
                {translate("leave.apply")}
              </Button>
          </Flex>
          <Table
            showSorterTooltip={false}
            rowKey="id"
            size={"middle"}
            dataSource={leaves}
            columns={columns}
            pagination={{
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
            }}
          />
        </Flex>
      </Card>
      </ReadOnlyWhenRestricted>
    </>
  );
};
export default TimeOffPage;
